"use client"

import {
    createUsZipCodeValidator,
    noLettersZipCodeValidator
} from "@/app/(site)/_components/Atoms/Inputs/errors/fieldValidators"
import Input, { type InputProps } from "@/app/(site)/_components/Atoms/Inputs/Input"
import { usePfasValidation } from "@/app/(site)/_utils/_hooks/usePfasValidation"
import { t } from "@/translations"

interface InputZipCodeProps extends Omit<InputProps, "type"> {
    country?: string
    state?: string
    langCode: string
    isLoading?: boolean
    containerClassName?: string
    selectedCountryCode: string
    label: string
    tabIndex?: number
    autoComplete?: string
    value: string
    disablePfasValidation?: boolean
}

const ZIP_CODE_LABEL_KEYS: Record<string, string> = {
    us: "checkout.information_for_billing_and_shipping.zip_code_us",
    gb: "checkout.information_for_billing_and_shipping.zip_code_gb",
    au: "checkout.information_for_billing_and_shipping.zip_code_au"
}

const US_ZIP_MAX_LENGTH = 5

export default function InputZipCode({
    country = "",
    state = "",
    maxLength = 10,
    typography = "Trade_Gothic_Text_16_150_0_300",
    langCode,
    onBlurValidation = false,
    isLoading,
    containerClassName,
    selectedCountryCode,
    label,
    tabIndex,
    autoComplete = "",
    value,
    disablePfasValidation = false,
    customValidators,
    ...props
}: InputZipCodeProps) {
    const { isPfasRestricted } = usePfasValidation({
        country,
        state,
        zipCode: value,
        disabled: disablePfasValidation
    })

    const countryKey = selectedCountryCode.toLowerCase()
    const zipMaxLength = countryKey === "us" ? US_ZIP_MAX_LENGTH : maxLength

    return (
        <Input
            type="text"
            inputMode="text"
            maxLength={zipMaxLength}
            typography={typography}
            countryCode={selectedCountryCode}
            /**
             * A restriction is a fact about the basket rather than about what was typed,
             * so it arrives as an error the field is told to hold rather than one it finds.
             */
            externalErrors={isPfasRestricted ? ["pfas_restricted"] : []}
            customValidators={[
                createUsZipCodeValidator(state),
                noLettersZipCodeValidator,
                ...(customValidators ?? [])
            ]}
            langCode={langCode}
            onBlurValidation={onBlurValidation}
            isLoading={isLoading}
            containerClassName={containerClassName}
            label={
                t(
                    langCode,
                    ZIP_CODE_LABEL_KEYS[countryKey] ??
                        "checkout.information_for_billing_and_shipping.zip_code"
                ) || label
            }
            tabIndex={tabIndex}
            autoComplete={autoComplete}
            value={value}
            {...props}
        />
    )
}
